/**
 * Outputs to look at (decision 0050): which artifacts of an item are outputs, their version chains, the picks on a
 * human review card, what `informs` hands on, and the pack attachment paths. Pure; shared by core, the cloud and the
 * web.
 */
import {
  type ArtifactKind as ArtifactKindSchema,
  FILE_TYPES,
  type FileContentType,
  isFileContentType,
  shaOfFileUri,
} from "@minga/protocol"
import { mergeablePrOf, runMergeOf } from "./github"
import { artifactOf, isOpenProposal, itemOf } from "./state"
import { ancestorsOf, descendantsOf, isContainer } from "./tree"
import type {
  Artifact,
  Ids,
  Item,
  ItemId,
  OptionEffect,
  ProjectState,
  Proposal,
  QuestionOption,
} from "./types"

type ArtifactKind = typeof ArtifactKindSchema.Type
type ArtifactId = Ids["ArtifactId"]

/** Kinds that are "outputs to look at": they get picks on a human review card and previews. */
export const OUTPUT_KINDS: ReadonlySet<ArtifactKind> = new Set<ArtifactKind>([
  "image",
  "report",
  "document",
  "video",
])

/**
 * An output: an OUTPUT_KINDS artifact with a file URI (`shaOfFileUri(uri) !== null`). A link (https://…) never is, and
 * neither is a document (wave DOC, decision 0068: `doc` set): never a review pick, never in "To look at" or Plan ›
 * Outputs' work list.
 */
export const isOutput = (a: Artifact): boolean =>
  OUTPUT_KINDS.has(a.kind) && shaOfFileUri(a.uri) !== null && a.doc === undefined

/** At most this many pick options (+ Approve all, Request changes, Cancel = 8, QuestionOption max). */
export const MAX_PICKS = 5
export const PICK_PREFIX = "approve:"
export const pickKey = (artifactId: ArtifactId): string => `${PICK_PREFIX}${artifactId}`
export const pickedArtifactId = (key: string | null): ArtifactId | null =>
  key?.startsWith(PICK_PREFIX) && key.length > PICK_PREFIX.length
    ? (key.slice(PICK_PREFIX.length) as ArtifactId)
    : null
/** Decision 0059: the review card's Merge option (B2 draws it as the primary button). Keep the key exactly. */
export const MERGE_KEY = "merge"

/** "approve" or "approve:<id>": the human stage passes (ledger). */
export const isApproveKey = (key: string | null): boolean =>
  key === "approve" || pickedArtifactId(key) !== null

/** The non-rejected artifact that supersedes `id`, if any. */
export const supersederOf = (s: Pick<ProjectState, "artifacts">, id: ArtifactId): Artifact | undefined =>
  s.artifacts.find((x) => x.supersedes === id && x.state !== "rejected")

/** The version chain ending in `a`, oldest first (follows `supersedes`; stops at a missing id or a cycle). */
export const versionsOf = (s: Pick<ProjectState, "artifacts">, a: Artifact): ReadonlyArray<Artifact> => {
  const chain: Artifact[] = [a]
  const seen = new Set<string>([a.id])
  let cur = a
  while (cur.supersedes !== null) {
    const id = cur.supersedes
    const prev = s.artifacts.find((x) => x.id === id)
    if (!prev || seen.has(prev.id)) break
    seen.add(prev.id)
    chain.unshift(prev)
    cur = prev
  }
  return chain
}

/** Not rejected and not superseded by a non-rejected artifact. */
export const isLive = (s: Pick<ProjectState, "artifacts">, a: Artifact): boolean =>
  a.state !== "rejected" && !supersederOf(s, a.id)

/**
 * The artifact an `O-n` names (one ref per version chain, decision 0065): the chain's head, i.e. the newest version
 * that is not rejected and not superseded; else the newest version. Case-insensitive; undefined when no artifact has
 * the ref.
 */
export const artifactByRef = (s: Pick<ProjectState, "artifacts">, ref: string): Artifact | undefined => {
  const r = ref.trim().toUpperCase()
  let newest: Artifact | undefined
  let head: Artifact | undefined
  for (const a of s.artifacts) {
    if (a.ref === undefined || a.ref.toUpperCase() !== r) continue
    newest = a
    if (isLive(s, a)) head = a
  }
  return head ?? newest
}

/** True when `a` is what its ref names ({@link artifactByRef}); true for an artifact without a ref. */
export const isChainHead = (s: Pick<ProjectState, "artifacts">, a: Artifact): boolean =>
  a.ref === undefined || artifactByRef(s, a.ref)?.id === a.id

/** `O-14@v2`: a version of a chain that is not its head (`read` takes it; search folds it under the head). */
export const VERSION_ANCHOR = /^(O-[1-9]\d*)@v([1-9]\d*)$/i

/**
 * What `read` takes for an artifact: its ref for the chain's head, `O-n@v<k>` for an older version (k = its place in
 * its chain, oldest = 1), `output:<id>` without a ref.
 */
export const outputAnchor = (s: Pick<ProjectState, "artifacts">, a: Artifact): string =>
  a.ref === undefined ? `output:${a.id}` : isChainHead(s, a) ? a.ref : `${a.ref}@v${versionsOf(s, a).length}`

/** The artifact an `O-n@v<k>` anchor names: version k of the chain that ends in the ref's head; undefined if none. */
export const artifactByVersionAnchor = (
  s: Pick<ProjectState, "artifacts">,
  anchor: string,
): Artifact | undefined => {
  const m = VERSION_ANCHOR.exec(anchor.trim())
  if (!m) return undefined
  const head = artifactByRef(s, m[1] ?? "")
  if (!head) return undefined
  const k = Number(m[2])
  const inChain = versionsOf(s, head)[k - 1]
  if (inChain) return inChain
  // a rejected branch is not on the head's chain: the newest version of the ref at that place
  return [...s.artifacts].reverse().find((a) => a.ref === head.ref && versionsOf(s, a).length === k)
}

/** The item's outputs now: `isOutput`, not rejected, not superseded by a non-rejected artifact; oldest first. */
export const liveOutputs = (s: ProjectState, itemId: ItemId): ReadonlyArray<Artifact> =>
  s.artifacts.filter((a) => a.itemId === itemId && isOutput(a) && isLive(s, a))

/** A code review: the item has a non-rejected pull_request artifact (no picks then, decision 0050). */
export const isCodeReview = (s: ProjectState, itemId: ItemId): boolean =>
  s.artifacts.some((a) => a.itemId === itemId && a.kind === "pull_request" && a.state !== "rejected")

const short = (title: string, max = 40): string =>
  title.length > max ? `${title.slice(0, max - 1).trimEnd()}…` : title

const option = (key: string, label: string, effects: ReadonlyArray<OptionEffect>): QuestionOption => ({
  key,
  label,
  detail: null,
  effects,
})

/** The pick effects: `finals` final, `rejects` rejected, and every older version of either that is not rejected. */
const pickEffects = (
  s: ProjectState,
  finals: ReadonlyArray<Artifact>,
  rejects: ReadonlyArray<Artifact>,
): ReadonlyArray<OptionEffect> => {
  const out: OptionEffect[] = []
  const seen = new Set<string>()
  const set = (a: Artifact, state: "final" | "rejected") => {
    if (seen.has(a.id)) return
    seen.add(a.id)
    out.push({ _tag: "SetArtifactState", artifactId: a.id, state })
  }
  for (const a of finals) set(a, "final")
  for (const a of rejects) set(a, "rejected")
  for (const v of [...finals, ...rejects])
    for (const older of versionsOf(s, v).slice(0, -1)) if (older.state !== "rejected") set(older, "rejected")
  return out
}

/**
 * The approve options of a human review card, always non-empty and always containing the key "approve":
 * [plain approve] | [approve with effects] | [approve:<id> × 2–5, approve "Approve all"]. Decision 0059: a code review
 * whose PR a Minga run merged asks to confirm ([approve "Confirm"]); one with a watched open PR of its own offers
 * [merge "Merge", approve "Approve without merging"].
 */
export const reviewApproveOptions = (s: ProjectState, item: Item): ReadonlyArray<QuestionOption> => {
  if (isCodeReview(s, item.id) || runMergeOf(s, item) !== undefined) {
    if (runMergeOf(s, item) !== undefined) return [option("approve", "Confirm", [])]
    if (mergeablePrOf(s, item) !== undefined)
      return [option(MERGE_KEY, "Merge", []), option("approve", "Approve without merging", [])]
    return [option("approve", "Approve", [])]
  }
  const outputs = liveOutputs(s, item.id)
  if (outputs.length === 0 || outputs.length > MAX_PICKS) return [option("approve", "Approve", [])]
  const [only] = outputs
  if (outputs.length === 1 && only) return [option("approve", "Approve", pickEffects(s, [only], []))]
  return [
    ...outputs.map((x) =>
      option(
        pickKey(x.id),
        `Approve ${short(x.title)}`,
        pickEffects(
          s,
          [x],
          outputs.filter((y) => y.id !== x.id),
        ),
      ),
    ),
    option("approve", "Approve all", pickEffects(s, outputs, [])),
  ]
}

export interface ReviewOutput {
  readonly artifact: Artifact
  /** Oldest first; ends with `artifact`. Length 1 = no older version. */
  readonly versions: ReadonlyArray<Artifact>
  /**
   * The card's option that picks it: "approve:<id>" (2–5 outputs), "approve" (the single output of a one-output card),
   * or null (no pick: a code review, more than 5 outputs).
   */
  readonly optionKey: string | null
}

/** A human review card: a question about an item whose intentKey starts with `review:<itemId>:`. */
const reviewItemOf = (p: Proposal): ItemId | null => {
  if (p.body._tag !== "question" || p.body.subject?._tag !== "item") return null
  const id = p.body.subject.id
  return p.intentKey.startsWith(`review:${id}:`) ? id : null
}

/** The artifacts an option's effects set `final`. */
const finalsOf = (o: QuestionOption): ReadonlyArray<ArtifactId> =>
  o.effects.flatMap((e) => (e._tag === "SetArtifactState" && e.state === "final" ? [e.artifactId] : []))

/**
 * The review card payload: the artifacts a human review card is about. Artifacts named by its options' `final`
 * effects (even if rejected since: shown greyed), then the item's other live outputs (a code review's, or > 5); each
 * with its version chain. [] for any other proposal. Used by the web card, read_proposal and the Overview (slice 3).
 */
export const reviewOutputs = (s: ProjectState, p: Proposal): ReadonlyArray<ReviewOutput> => {
  const itemId = reviewItemOf(p)
  if (itemId === null || p.body._tag !== "question") return []
  const options = p.body.options
  const named: ArtifactId[] = []
  for (const o of options) for (const id of finalsOf(o)) if (!named.includes(id)) named.push(id)
  const keyOf = (id: ArtifactId): string | null => {
    const pick = options.find((o) => pickedArtifactId(o.key) === id)
    if (pick) return pick.key
    const approve = options.find((o) => o.key === "approve")
    const finals = approve ? finalsOf(approve) : []
    return finals.length === 1 && finals[0] === id && !options.some((o) => pickedArtifactId(o.key) !== null)
      ? "approve"
      : null
  }
  const out: ReviewOutput[] = []
  for (const id of named) {
    const a = artifactOf(s, id)
    if (a) out.push({ artifact: a, versions: versionsOf(s, a), optionKey: keyOf(id) })
  }
  for (const a of liveOutputs(s, itemId))
    if (!named.includes(a.id)) out.push({ artifact: a, versions: versionsOf(s, a), optionKey: null })
  return out
}

/** The open human review cards of an item (intentKey starting `review:<itemId>:`). */
export const openReviewCards = (s: ProjectState, itemId: ItemId): ReadonlyArray<Proposal> =>
  s.proposals.filter((p) => isOpenProposal(p) && reviewItemOf(p) === itemId)

/**
 * What `informs` hands on (was `inputsOf` in context.ts; used by context.ts and read_context): artifacts of the source
 * items (and of a container's done descendants), not rejected and not superseded by a non-rejected artifact; plus the
 * source items (for notes). Sources are the items with an `informs` edge into the item or any item above it.
 */
export const handedOn = (
  s: ProjectState,
  item: Item,
): { readonly artifacts: ReadonlyArray<Artifact>; readonly sources: ReadonlyArray<Item> } => {
  const gated = new Set<string>([item.id, ...ancestorsOf(s, item).map((a) => a.id)])
  const sources: Item[] = []
  for (const e of s.edges) {
    if (e.kind !== "informs" || !gated.has(e.to.id)) continue
    const src = itemOf(s, e.from.id as ItemId)
    if (src && !sources.some((x) => x.id === src.id)) sources.push(src)
  }
  const artifacts: Artifact[] = []
  const add = (a: Artifact) => {
    if (isLive(s, a) && !artifacts.some((x) => x.id === a.id)) artifacts.push(a)
  }
  for (const src of sources) {
    for (const a of s.artifacts) if (a.itemId === src.id) add(a)
    if (isContainer(s, src)) {
      const done = new Set(
        descendantsOf(s, src.id)
          .filter((d) => d.status === "done")
          .map((d) => d.id),
      )
      for (const a of s.artifacts) if (a.itemId !== null && done.has(a.itemId)) add(a)
    }
  }
  return { artifacts, sources }
}

/** Pack attachments (decision 0050). */
export const ATTACHMENTS_MAX_FILES = 10
export const ATTACHMENTS_MAX_BYTES = 50 * 1024 * 1024
const ATTACHMENTS_DIR = ".memory/attachments/"

/**
 * `slug`: lowercase, every run of characters outside [a-z0-9] → "-", trimmed of "-", ≤ 40 characters, "file" if empty.
 * Both paths always match `AttachmentPath` (§4.4).
 */
export const fileSlug = (name: string): string => {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "")
  return slug === "" ? "file" : slug
}

const extOf = (contentType: string | null): string =>
  contentType !== null && isFileContentType(contentType) ? FILE_TYPES[contentType].ext : "bin"

/**
 * Decision 0150: an input's file name under `.minga/inputs/<ref>/`: the title's own extension when it has one (a source
 * file stored as text/plain keeps `.yaml`, `.py`), else the content type's; the rest slugged.
 */
export const inputNameOf = (a: Pick<Artifact, "title" | "contentType">): string => {
  const own = /\.([A-Za-z0-9]{1,10})$/.exec(a.title.trim())
  const base = own ? a.title.trim().slice(0, -own[0].length) : a.title
  return `${fileSlug(base)}.${own ? (own[1] as string).toLowerCase() : extOf(a.contentType)}`
}

/** `.memory/attachments/<artifactId>-<slug(title)>.<FILE_TYPES[contentType].ext>`. */
export const attachmentPathOf = (a: Artifact): string =>
  `${ATTACHMENTS_DIR}${a.id}-${fileSlug(a.title)}.${extOf(a.contentType)}`

/** A chat file: `.memory/attachments/chat-<sha256 first 12>-<slug(filename without its extension)>.<ext>`. */
export const chatAttachmentPath = (
  sha256: string,
  filename: string | null,
  contentType: FileContentType,
): string => {
  const base = (filename ?? "").replace(/\.[^.]*$/, "")
  return `${ATTACHMENTS_DIR}chat-${sha256.slice(0, 12)}-${fileSlug(base)}.${FILE_TYPES[contentType].ext}`
}
